<template>
  <ManagerLayout>
    <div class="manager-users">
      <h2 class="page-title">ユーザー一覧</h2>
      <p class="page-desc">保護者・講師・管理者の一覧。ロール・サブスク・bypass でフィルタし、メール・氏名で検索できます。</p>

      <!-- フィルタ・検索 -->
      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">フィルタ・検索</h3>
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">ロール</span>
              <select v-model="filterRole" class="form-select" @change="loadUsers">
                <option value="all">すべて</option>
                <option value="adult">保護者</option>
                <option value="instructor">講師</option>
                <option value="manager">管理者</option>
              </select>
            </label>
            <label class="filter-item">
              <span class="filter-label">サブスク</span>
              <select v-model="filterSubscription" class="form-select" @change="loadUsers">
                <option value="all">すべて</option>
                <option value="active">有料</option>
                <option value="trialing">トライアル</option>
                <option value="none">なし</option>
              </select>
            </label>
            <label class="filter-item">
              <span class="filter-label">bypass</span>
              <select v-model="filterBypass" class="form-select" @change="loadUsers">
                <option value="all">すべて</option>
                <option value="yes">付与中</option>
                <option value="no">なし</option>
              </select>
            </label>
            <label class="filter-item filter-search">
              <span class="filter-label">検索</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="メール・氏名で部分一致"
                class="form-input"
                @keyup.enter="loadUsers"
              />
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadUsers">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <!-- 一覧 -->
      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ total }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadUsers">
              更新
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!users.length" class="empty-state">該当するユーザーがいません</div>
          <div v-else class="table-wrap users-table-wrap">
            <table class="data-table" aria-label="ユーザー一覧">
              <thead>
                <tr>
                  <th>メール</th>
                  <th>氏名</th>
                  <th>ロール</th>
                  <th>サブスク</th>
                  <th>bypass</th>
                  <th class="num">current_day</th>
                  <th>登録日時（JST）</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in users" :key="u.id" class="data-row">
                  <td>{{ u.email }}</td>
                  <td>{{ u.fullName || '—' }}</td>
                  <td><span class="role-badge" :class="u.role">{{ u.roleLabel }}</span></td>
                  <td>{{ u.subscriptionLabel }}</td>
                  <td>
                    <span v-if="u.bypassSubscription" class="bypass-badge">あり</span>
                    <span v-else>—</span>
                  </td>
                  <td class="num">
                    {{ u.displayCurrentDay ?? u.currentDay }}
                    <span v-if="u.hasChildren" class="day-source">（子）</span>
                  </td>
                  <td>{{ formatDateTimeJst(u.createdAt) }}</td>
                  <td class="action">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline"
                      :disabled="updatingUserId === u.id"
                      @click="openDayModal(u)"
                    >
                      {{ updatingUserId === u.id ? '更新中...' : 'Dayを変更' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="users.length > 0 && users.length < total" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Day変更モーダル -->
      <div v-if="dayModalUser" class="modal-overlay" @click.self="closeDayModal">
        <div class="modal-card">
          <h3 class="modal-title">current_day を変更</h3>
          <p class="modal-desc">
            {{ dayModalUser.email }}（{{ dayModalUser.fullName || '—' }}）の Day を変更します。
          </p>
          <p v-if="dayModalUser.hasChildren" class="modal-note">
            このアカウントは子がいます。アプリで表示されるのは子の進度です。子の Day を変えるには「学習者一覧」で該当の子の「Dayを変更」を使ってください。ここではアカウント（users_phase2）の current_day のみ更新されます。
          </p>
          <div class="modal-form">
            <label class="form-label">
              <span>新しい Day（1 以上の整数）</span>
              <input
                v-model.number="dayModalValue"
                type="number"
                min="1"
                step="1"
                class="form-input num-input"
                @keyup.enter="submitDayChange('user')"
              />
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeDayModal">キャンセル</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!isValidDay(dayModalValue) || savingDay"
              @click="submitDayChange('user')"
            >
              {{ savingDay ? '保存中...' : '変更する' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 100

const filterRole = ref('all')
const filterSubscription = ref('all')
const filterBypass = ref('all')
const searchQ = ref('')
const users = ref([])
const total = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const currentOffset = ref(0)
const updatingUserId = ref(null)
const dayModalUser = ref(null)
const dayModalValue = ref(1)
const savingDay = ref(false)

function isValidDay (v) {
  const n = typeof v === 'number' ? v : parseInt(v, 10)
  return Number.isInteger(n) && n >= 1
}

function openDayModal (u) {
  dayModalUser.value = u
  dayModalValue.value = u.currentDay ?? 1
}

function closeDayModal () {
  dayModalUser.value = null
  dayModalValue.value = 1
}

async function submitDayChange (target) {
  if (target !== 'user' || !dayModalUser.value) return
  const userId = dayModalUser.value.id
  const currentDay = typeof dayModalValue.value === 'number' ? dayModalValue.value : parseInt(dayModalValue.value, 10)
  if (!isValidDay(currentDay)) return
  savingDay.value = true
  updatingUserId.value = userId
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/users/${encodeURIComponent(userId)}/current-day`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ currentDay })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '更新に失敗しました')
      return
    }
    const idx = users.value.findIndex((u) => u.id === userId)
    if (idx !== -1) users.value[idx].currentDay = data.currentDay
    closeDayModal()
  } finally {
    savingDay.value = false
    updatingUserId.value = null
  }
}

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

function buildQueryParams (offset = 0) {
  const params = new URLSearchParams()
  if (filterRole.value !== 'all') params.set('role', filterRole.value)
  if (filterSubscription.value !== 'all') params.set('subscription', filterSubscription.value)
  if (filterBypass.value !== 'all') params.set('bypass', filterBypass.value)
  if (searchQ.value.trim()) params.set('q', searchQ.value.trim())
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

const loadUsers = async () => {
  loading.value = true
  currentOffset.value = 0
  try {
    const token = await getToken()
    const qs = buildQueryParams(0)
    const res = await fetch(`${apiBase}/api/manager/users?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      users.value = []
      total.value = 0
      return
    }
    users.value = data.users || []
    total.value = data.total ?? 0
    currentOffset.value = users.value.length
  } catch (_) {
    users.value = []
    total.value = 0
    currentOffset.value = 0
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  const nextOffset = currentOffset.value
  if (nextOffset >= total.value) return
  loadingMore.value = true
  try {
    const token = await getToken()
    const qs = buildQueryParams(nextOffset)
    const res = await fetch(`${apiBase}/api/manager/users?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.users || []
    users.value = [...users.value, ...list]
    currentOffset.value = nextOffset + list.length
  } finally {
    loadingMore.value = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.manager-users {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.75rem 0;
  font-size: 1.1rem;
  color: #334155;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.filter-search .form-input {
  min-width: 200px;
}

.form-select,
.form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.btn {
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.data-table th.num,
.data-table td.num {
  text-align: right;
}

.data-table th.action,
.data-table td.action {
  white-space: nowrap;
}

.btn-outline {
  background: transparent;
  border: 1px solid #94a3b8;
  color: #475569;
}

.btn-outline:hover:not(:disabled) {
  background: #f1f5f9;
}

.role-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
}

.role-badge.adult {
  background: #dbeafe;
  color: #1e40af;
}

.role-badge.instructor {
  background: #dcfce7;
  color: #166534;
}

.role-badge.manager {
  background: #fef3c7;
  color: #92400e;
}

.bypass-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  background: #fce7f3;
  color: #9d174d;
}

.load-more {
  margin-top: 1rem;
  text-align: center;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  min-width: 320px;
  max-width: 90vw;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  color: #334155;
}

.modal-desc {
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
}

.modal-note {
  margin: 0 0 1rem 0;
  font-size: 0.85rem;
  color: #b45309;
  background: #fffbeb;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
}

.day-source {
  font-size: 0.8rem;
  color: #64748b;
  margin-left: 0.25rem;
}

.modal-form {
  margin-bottom: 1rem;
}

.modal-form .form-label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.9rem;
  color: #475569;
}

.modal-form .num-input {
  width: 6rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>
